import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormGroup, FormsModule } from '@angular/forms';
import { TabsInnerWrapComponent } from '../../components/tabs-inner-wrap/tabs-inner-wrap.component';
import { DetailsSectionComponent } from '../../components/details-container/details-section.component';
import { OverallSectionComponent } from './overall-section/overall-section.component';
import { MessagingSectionComponent } from './messaging-section/messaging-section.component';
import { MethodologySectionComponent } from './methodology-section/methodology-section.component';
import { SkillSectionComponent } from './skills-section/skill-section.component';
import { AuthorReactiveFormService } from '../../services/author-reactive-form.service';
import { SelfAuthoringPersonaFormService } from '../../services/self-authoring-persona-form.service';
import { ActivatedRoute } from '@angular/router';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { IScoreWeight } from 'src/app/interfaces/self-serve/isimulation-score';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { QaiButtonComponent, QaiFloatingDirective, QaiTooltipComponent } from '@qai/ui';
import { ScoreLastSessionModalComponent } from '../../components/modals/score-last-session-modal/score-last-session-modal.component';
import { AccuracyFeedbackSectionComponent } from './accuracy-feedback-section/accuracy-feedback-section.component';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { AppConstants } from 'src/app/app-constants';
import { MessageBlockComponent } from 'src/app/components/shared/message-block/message-block.component';
import { hasNoMessagingThemes, hasIncompleteMessagingTheme } from 'src/app/utils/messaging-facts.util';
import { hasMissingSkillsRubric } from 'src/app/utils/skills-rubric.util';
import { CallRefinementPollingService } from 'src/app/services/self-serve/call-refinement-polling.service';
import { ScoringRefinementService } from 'src/app/services/self-serve/scoring-refinement.service';

@Component({
  selector: 'tab-scoring-details',
  standalone: true,
  templateUrl: './scoring-details.component.html',
  styleUrls: ['./scoring-details.component.scss'],
  imports: [
    FormsModule,
    CommonModule,
    TabsInnerWrapComponent,
    DetailsSectionComponent,
    OverallSectionComponent,
    MessagingSectionComponent,
    MethodologySectionComponent,
    SkillSectionComponent,
    TranslocoModule,
    QaiButtonComponent,
    QaiTooltipComponent,
    QaiFloatingDirective,
    ScoreLastSessionModalComponent,
    AccuracyFeedbackSectionComponent,
    MessageBlockComponent,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class ScoringDetailsComponent {
  form!: FormGroup;
  formWeightSection!: FormGroup;
  /** Full simulation form — the messaging-facts checks need more than the scoring section. */
  private rootForm?: FormGroup;

  callUuid: string = '';
  simulationName: string = '';
  moduleUuid: string = '';
  replicaId: string = '';
  showScoreLastSessionModal: boolean = false;

  isSelfAuthoring = input(false);

  skillsDisabled: boolean = false;
  methodolgyDisabled: boolean = false;
  messagingDisabled: boolean = false;

  /**
   * The toggle is a self-authoring roleplay surface only; accuracy on other
   * sims is turned on through ops tooling rather than here.
   */
  get showAccuracyFeedback(): boolean {
    return this.accuracyFeedbackFlagEnabled && this.isSelfAuthoring();
  }

  private accuracyFeedbackFlagEnabled: boolean = false;

  private readonly selfAuthoringPersonaFormService = inject(SelfAuthoringPersonaFormService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly featureFlagsService = inject(FeatureFlagsService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly callRefinementPollingService = inject(CallRefinementPollingService);
  private readonly scoringRefinementService = inject(ScoringRefinementService);

  /** True while a refinement is scheduled (ScoringRefinementService debounce) or still running (polling). */
  get isRefinementInProgress(): boolean {
    return (
      this.callRefinementPollingService.isRefining(this.callUuid) ||
      this.scoringRefinementService.isPending(this.callUuid)
    );
  }

  // Same messaging-facts rules that gate Publish (SimulationDetailsComponent) and Send test assignments.
  get isV2WithNoFacts(): boolean {
    const promptType = this.rootForm?.get(`${SimulationConstants.MESSAGING_PROMPT_PATH}.promptType`)?.value;
    const factSections = this.rootForm?.get(
      `${SimulationConstants.SCORING_SECTION}.${SimulationConstants.MESSAGING_THEME_SECTION}.${SimulationConstants.FACT_SECTIONS_SECTION}`
    ) as FormArray | null;
    return (
      promptType === SimulationConstants.MESSAGING_PROMPT_TYPE_V2 &&
      (!factSections || factSections.length === 0)
    );
  }

  get isMissingMessagingThemes(): boolean {
    return this.isSelfAuthoring() && !!this.rootForm && hasNoMessagingThemes(this.rootForm);
  }

  get hasEmptyFacts(): boolean {
    return this.isSelfAuthoring() && !!this.rootForm && hasIncompleteMessagingTheme(this.rootForm);
  }

  get isMissingSkillsRubric(): boolean {
    return this.isSelfAuthoring() && !!this.rootForm && hasMissingSkillsRubric(this.rootForm);
  }

  get isMissingMethodology(): boolean {
    return (
      this.isSelfAuthoring() &&
      !this.methodolgyDisabled &&
      !this.rootForm?.get(`${SimulationConstants.METHODOLOGY_PROMPT_PATH}.rubric`)?.value
    );
  }

  get isScoreLastSessionDisabled(): boolean {
    return (
      this.isRefinementInProgress ||
      this.isV2WithNoFacts ||
      this.isMissingMessagingThemes ||
      this.hasEmptyFacts ||
      this.isMissingSkillsRubric ||
      this.isMissingMethodology
    );
  }

  constructor(
    private authorReactiveFormService: AuthorReactiveFormService,
    private fb: FormBuilder,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.callUuid = this.route.snapshot.paramMap.get('uuid') ?? '';
    if (this.callUuid) {
      this.initializeForm();
    }
    this.resolveAccuracyFeedbackVisibility();
  }

  private async resolveAccuracyFeedbackVisibility(): Promise<void> {
    this.accuracyFeedbackFlagEnabled =
      await this.featureFlagsService.isFlagEnabled(
        AppConstants.ACCURACY_FEEDBACK
      );
    // the await resolves after the initial change detection pass
    this.cdRef.detectChanges();
  }

  private initializeForm(): void {
    this.authorReactiveFormService.getForm(this.callUuid).subscribe((form) => {
      this.simulationName =
        form?.get(`${SimulationConstants.GENERAL_SECTION}.simulationName`)?.value ?? '';
      this.moduleUuid = form?.get(SimulationConstants.MODULE_UUID_PATH)?.value ?? '';
      this.replicaId =
        form?.get(`${SimulationConstants.CALL_PERSONA_PATH}.replicaId`)?.value ?? '';
      this.rootForm = form;
      this.form = form?.get(SimulationConstants.SCORING_SECTION) as FormGroup;
      this.formWeightSection = this.form?.get(
        SimulationConstants.SCORING_WEIGHTS_SECTION
      ) as FormGroup;

      if (this.formWeightSection) {
        this.setupWeightValueChange(this.formWeightSection);
        this.updateSectionDisableStates(this.formWeightSection.value);
      }
    });

    this.selfAuthoringPersonaFormService
      .getOrCreateForm(this.callUuid)
      .get('replicaId')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((replicaId: string) => {
        if (replicaId) {
          this.replicaId = replicaId;
        }
      });
  }

  private setupWeightValueChange(section: FormGroup): void {
    section.valueChanges
      .pipe(
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe((updatedValue: IScoreWeight[]) => {
        console.log('Updated value in scoring details', updatedValue);
        this.updateSectionDisableStates(updatedValue);
      });
  }

  private updateSectionDisableStates(formWeightValue: IScoreWeight[]) {
    formWeightValue.forEach((item: { section: any; disabled: boolean }) => {
      switch (item.section) {
        case 'Methodology':
          this.methodolgyDisabled = item.disabled;
          break;
        case 'Messaging':
          this.messagingDisabled = item.disabled;
          break;
        case 'Skills':
          this.skillsDisabled = item.disabled;
          break;
      }
    });
  }

  scoreLastSession(): void {
    if (this.isScoreLastSessionDisabled) return;
    this.showScoreLastSessionModal = true;
  }
}
